import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, take } from 'rxjs';
import { UserService } from 'src/app/core/services/user.service';
import { redirectToLogin } from '../../../core/utils/login-redirect.util';
import { rememberLoginReturn } from '../../../core/utils/login-return.util';
import { readRelaymdRequest, relaymdAccount, relaymdReturnUrl, type RelaymdAccount, type RelaymdRequest } from '../../../core/utils/relaymd-sign-in.util';

/**
 * `/relaymd/sign-in`: where RelayMD's "Sign in with MedicAI" opens. A signed-in user confirms and is handed back to the
 * app with a one-use token; anyone else signs in here first and the login comes back here.
 *
 * The person always confirms, seeing which account the app will get. Without that, any app on the phone could open this
 * page itself and silently receive a token for whoever is signed in. "Use a different account" signs out of MedicAI in
 * this browser and comes back here after the new login.
 */
@Component({
  selector: 'app-relaymd-sign-in',
  templateUrl: './relaymd-sign-in.component.html',
  styleUrls: ['./relaymd-sign-in.component.scss']
})
export class RelaymdSignInComponent implements OnInit {
  message: string | null = null;
  signInFirst = false;
  account: RelaymdAccount | null = null;
  sending = false;
  request: RelaymdRequest | null = null;

  constructor(private http: HttpClient, private userService: UserService, private route: ActivatedRoute, private router: Router) { }

  ngOnInit(): void {
    const query = new URLSearchParams();
    this.route.snapshot.queryParamMap.keys.forEach((key) => query.set(key, this.route.snapshot.queryParamMap.get(key) ?? ''));
    this.request = readRelaymdRequest(query);
    if (!this.request) {
      this.message = 'This sign-in link is not complete. Start again from the RelayMD app.';
      return;
    }
    if (!this.userService.validateToken()) {
      this.ask('Sign in to MedicAI first. You will come straight back here.');
      return;
    }
    // the profile loads after the page opens, so wait for it rather than read it now
    this.userService.userProfile$.pipe(filter(Boolean), take(1)).subscribe((user) => {
      this.account = relaymdAccount(user) ?? { name: 'your MedicAI account', email: null, initials: '?' };
    });
  }

  continue(): void {
    if (!this.request || this.sending) return;
    this.sending = true;
    const { challenge, state } = this.request;
    this.http.post<{ token: string }>('api/relaymd/handoff', { challenge }).subscribe({
      next: ({ token }) => window.location.replace(relaymdReturnUrl(token, state)),
      error: (error) => {
        this.sending = false;
        const code = error?.error?.error;
        if (code === 'SIGN_IN_AGAIN' || error?.status === 401) {
          this.ask('Please sign in to MedicAI again. You will come straight back here.');
        } else if (error?.status === 404) {
          this.message = 'RelayMD sign-in is not switched on here.';
        } else {
          this.message = 'This MedicAI account cannot sign in to RelayMD.';
        }
      }
    });
  }

  switchAccount(): void {
    this.userService.logout();
    this.signIn();
  }

  signIn(): void {
    rememberLoginReturn(this.router.url);
    redirectToLogin();
  }

  private ask(message: string): void {
    this.message = message;
    this.signInFirst = true;
  }
}
